import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import "jspdf/dist/jspdf.es.min.js";
import axios from "axios";
import { FaMoneyBillWave, FaCreditCard, FaFileInvoiceDollar, FaRegCalendarAlt, FaPrint, FaTimes, FaCheckCircle, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { toast } from "react-hot-toast";
import MakbuzModal from "./MakbuzModal";
import { formatCurrencyInput, parseCurrency } from "../../utils/currency";

// Tarih formatını gün/ay/yıl şekline çeviren yardımcı fonksiyon
const formatDateDisplay = (dateString) => {
  if (!dateString) return "Belirtilmemiş";
  
  try {
    // YYYY-MM-DD formatını DD/MM/YYYY formatına çevir
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Geçersiz tarih ise olduğu gibi döndür
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    return dateString; // Hata durumunda orijinal değeri döndür
  }
};

const PaymentModal = ({ student, closeModal, onUpdate }) => {
  const [updatedStudent, setUpdatedStudent] = useState(student);

  // JWT token'dan kullanıcı adını al
  const getUserNameFromToken = () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const payload = token.split('.')[1];
        const decodedPayload = JSON.parse(atob(payload));
        return decodedPayload.data?.username || "Sistem";
      } catch (e) {
        console.error("JWT decode error:", e);
        return "Sistem";
      }
    }
    return "Sistem";
  };
  const [paymentAmount, setPaymentAmount] = useState("");
  const [selectedBill, setSelectedBill] = useState(null);
  const [paperSize, setPaperSize] = useState("A5");
  const [paymentType, setPaymentType] = useState("taksit");
  const [paymentMethod, setPaymentMethod] = useState("nakit");
  const [receiptNote, setReceiptNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showBillList, setShowBillList] = useState(true);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]); // Bugünün tarihi varsayılan
  const [isMakbuzModalOpen, setIsMakbuzModalOpen] = useState(false);
  const [makbuzData, setMakbuzData] = useState(null);
  const [paymentPerson, setPaymentPerson] = useState(""); // Ödeme yapan kişi
  const [paymentPersonSurname, setPaymentPersonSurname] = useState(""); // Ödeme yapan kişinin soyadı
  const [paymentError, setPaymentError] = useState(""); // Ödeme hatası mesajı
  
  // User role kontrolü
  const [userRole] = useState(localStorage.getItem("userRole") || "user");

  // Ödeme yapan kişi değiştiğinde soyadı alanını güncelle
  const handlePaymentPersonChange = (value) => {
    setPaymentPerson(value);
    
    // Varsayılan soyadı ayarla
    if (value === "anne") {
      setPaymentPersonSurname(student.surname || "");
    } else if (value === "baba") {
      setPaymentPersonSurname(student.surname || "");
    } else {
      setPaymentPersonSurname("");
    }
  };

  // Ödeme tutarı değiştiğinde hatayı temizle
  const handlePaymentAmountChange = (value) => {
    setPaymentAmount(value);
    setPaymentError(""); // Hata mesajını temizle
  };

  // Kısmi ödeme ID'si oluşturan helper fonksiyon
  const generatePartialPaymentId = (installmentId, existingPartials) => {
    // Bu taksit için mevcut kısmi ödeme sayısını bul
    const partialCount = existingPartials.filter(p => 
      p.originalInstallmentId === installmentId || 
      (p.id && typeof p.id === 'string' && p.id.includes(`partial-${installmentId}-`))
    ).length;
    
    // Format: "2-01", "2-02", "3-01" vb.
    const partialNumber = String(partialCount + 1).padStart(2, '0');
    return `${installmentId}-${partialNumber}`;
  };

  // User role için taksit filtresi - sadece en yakın tarihli taksiti göster
  const getAvailableBills = (unpaidBills) => {
    if (!Array.isArray(unpaidBills) || unpaidBills.length === 0) {
      return [];
    }

    // Admin tüm taksitleri görebilir
    if (userRole === "admin") {
      return unpaidBills;
    }

    // User sadece chronologically ilk taksiti görebilir (en erken vadeli)
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Bugünün başlangıcına ayarla
    
    // Tarihe göre sırala (en erken tarih ilk sırada)
    const sortedBills = [...unpaidBills].sort((a, b) => {
      const dateA = new Date(a.dueDate);
      const dateB = new Date(b.dueDate);
      return dateA - dateB;
    });
    
    
    return [sortedBills[0]]; // En erken tarihli taksit
  };

  // Kalan toplam taksit tutarını hesapla
  const getTotalRemainingAmount = (unpaidBills) => {
    if (!Array.isArray(unpaidBills)) return 0;
    return unpaidBills.reduce((total, bill) => total + (parseFloat(bill.amount) || 0), 0);
  };

  // Auth token alma fonksiyonu
  const getAuthToken = () => {
    return localStorage.getItem('authToken') || '';
  };

  // API isteği için gerekli headerları hazırla
  const getRequestHeaders = () => {
    const token = getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };
  
  // Ödeme planını parsela
  const parsedPaymentPlan = (() => {
    try {
      if (!student.payment_plan) {
        console.log("Ödeme planı bulunamadı!");
        return { 
          totalAmount: 0,
          installmentCount: 0,
          unpaidBills: [], 
          paidInstallments: [] 
        };
      }
      
      let paymentPlan = student.payment_plan;
   
      
      // String ise JSON'a dönüştürme işlemi
      if (typeof student.payment_plan === "string") {
   
        // Tırnak işaretlerini düzelt (çift tırnak içinde tek tırnak sorunu)
        let cleanedString = student.payment_plan
          .replace(/\\"/g, '"') // Kaçış karakterli çift tırnakları düzelt
          .replace(/"{/g, '{')  // Başlangıçtaki fazla tırnakları temizle
          .replace(/}"/g, '}'); // Sondaki fazla tırnakları temizle
          
        // Eğer başında ve sonunda çift tırnak varsa temizle
        if (cleanedString.startsWith('"') && cleanedString.endsWith('"')) {
          cleanedString = cleanedString.slice(1, -1);
        }
        
        
        
        try {
          paymentPlan = JSON.parse(cleanedString);
          
        } catch (parseError) {
          console.error("JSON parse hatası:", parseError);
          return { 
            totalAmount: 0, 
            installmentCount: 0,
            unpaidBills: [], 
            paidInstallments: [] 
          };
        }
      }
      
      // Gerekli alanları doğrula ve varsayılan değerler sağla
      const result = {
        totalAmount: paymentPlan.totalAmount || 0,
        installmentCount: paymentPlan.installmentCount || 0,
        unpaidBills: Array.isArray(paymentPlan.unpaidBills) ? paymentPlan.unpaidBills : [],
        paidInstallments: Array.isArray(paymentPlan.paidInstallments) ? paymentPlan.paidInstallments : []
      };
      
     
      return result;
    } catch (error) {
      console.error("Ödeme planı parse hatası:", error);
      toast.error("Ödeme planı verisi okunamadı!");
      return { 
        totalAmount: 0, 
        installmentCount: 0,
        unpaidBills: [], 
        paidInstallments: [] 
      };
    }
  })();

  // Ödenecek tutar state kontrolü
  useEffect(() => {
    // Eğer taksit seçilmişse, taksit tutarını otomatik ayarla
    if (selectedBill && paymentType === "taksit") {
      setPaymentAmount(selectedBill.amount.toString());
    }
  }, [selectedBill, paymentType]);

  const toggleBillList = () => {
    setShowBillList(!showBillList);
  };

  const resetForm = () => {
    setSelectedBill(null);
    setPaymentAmount("");
    setReceiptNote("");
    setShowConfirmation(false);
    setPaymentDate(new Date().toISOString().split('T')[0]); // Tarihi bugüne sıfırla
  };

  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);
    setSelectedBill(null);
    // Eğer taksit dışı ödeme seçilirse, ödeme tutarını sıfırla
    if (type !== "taksit") {
      setPaymentAmount("");
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    // Eğer zaten işlem devam ediyorsa engelle
    if (loading) {
      toast.warning("Ödeme işlemi devam ediyor, lütfen bekleyin...");
      return;
    }
    
    // Ödeme yapan kişi seçilmiş mi kontrol et
    if (!paymentPerson || paymentPerson.trim() === "") {
      toast.warning("Lütfen Ödemeyi Yapan Kişiyi Giriniz!", {
        duration: 4000,
        style: {
          background: '#FEF3C7',
          color: '#92400E',
          fontWeight: 'bold'
        }
      });
      return;
    }
    
    setLoading(true);

    try {
      // User role kontrolü - eski tarihli ödeme engelleme
      if (userRole === "user") {
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD formatında bugün
        if (paymentDate < today) {
          toast.error("Sadece bugün ve gelecek tarihli ödemeler alabilirsiniz.");
          setLoading(false);
          return;
        }
      }

      // Ödeme tutarını doğrula
      const amount = parseCurrency(paymentAmount);
      if (isNaN(amount) || amount <= 0) {
        toast.error("Lütfen geçerli bir ödeme tutarı girin");
        setLoading(false);
        return;
      }

      // Nakit ödeme için seçilen taksit tutarından fazla ödeme yapılamasın
      if (paymentType === "nakit" && selectedBill) {
        const selectedBillAmount = parseFloat(selectedBill.amount);
        if (amount > selectedBillAmount) {
          setPaymentError(`En fazla taksit tutarı kadar ödeme yapabilirsiniz!`);
          setLoading(false);
          return;
        }
      }

      // Öğrenci verilerini hazırla
      setUpdatedStudent((prevStudent) => {
        // Payment plan'ı doğru şekilde çözümle
        const parsedPaymentPlan = (() => {
          try {
            
            
            if (!prevStudent.payment_plan) {
              console.log("handlePaymentSubmit: payment_plan bulunamadı!");
              return { 
                totalAmount: 0,
                installmentCount: 0,
                unpaidBills: [], 
                paidInstallments: [] 
              };
            }
            
            let paymentPlan = prevStudent.payment_plan;
            
            if (typeof prevStudent.payment_plan === "string") {
              
              // Tırnak işaretlerini düzelt (çift tırnak içinde tek tırnak sorunu)
              let cleanedString = prevStudent.payment_plan
                .replace(/\\"/g, '"') // Kaçış karakterli çift tırnakları düzelt
                .replace(/"{/g, '{')  // Başlangıçtaki fazla tırnakları temizle
                .replace(/}"/g, '}'); // Sondaki fazla tırnakları temizle
                
              // Eğer başında ve sonunda çift tırnak varsa temizle
              if (cleanedString.startsWith('"') && cleanedString.endsWith('"')) {
                cleanedString = cleanedString.slice(1, -1);
              }
              

              
              try {
                paymentPlan = JSON.parse(cleanedString);
                
              } catch (parseError) {
                
                toast.error("Ödeme planı işlenirken hata oluştu");
                return { 
                  totalAmount: 0, 
                  installmentCount: 0,
                  unpaidBills: [], 
                  paidInstallments: [] 
                };
              }
            }
            
            // Üst bileşende çözümlenmiş payment_plan varsa onu kullan
            if (!paymentPlan.unpaidBills && window.parsedPaymentPlan) {
              
              return window.parsedPaymentPlan;
            }
            
            // Gerekli alanların var olduğundan emin ol
            return {
              totalAmount: paymentPlan.totalAmount || 0,
              installmentCount: paymentPlan.installmentCount || 0,
              unpaidBills: Array.isArray(paymentPlan.unpaidBills) ? paymentPlan.unpaidBills : [],
              paidInstallments: Array.isArray(paymentPlan.paidInstallments) ? paymentPlan.paidInstallments : []
            };
          } catch (error) {
            
            toast.error("Ödeme planı verisi okunamadı!");
            return { 
              totalAmount: 0, 
              installmentCount: 0,
              unpaidBills: [], 
              paidInstallments: [] 
            };
          }
        })();

        // Payment plan kontrolü ve debug logları
        
        
        if (!parsedPaymentPlan || !Array.isArray(parsedPaymentPlan.unpaidBills)) {
          
          toast.error("Ödeme planı geçersiz!");
          return prevStudent;
        }

        const { unpaidBills, paidInstallments = [] } = parsedPaymentPlan;
        // Seçilen ödeme tarihini kullan (YYYY-MM-DD formatında)
        const selectedPaymentDate = paymentDate;
        
        let updatedUnpaidBills = [...unpaidBills];
        let updatedPaidInstallments = [...paidInstallments];

        // Taksit ödemesi
        if (paymentType === "taksit" && selectedBill) {
          const paidBill = unpaidBills.find(bill => bill.id === selectedBill.id);
          
          if (!paidBill) {
            toast.error("Geçersiz taksit seçimi!");
            return prevStudent;
          }

          // Taksiti ödenen taksitlere ekle
          updatedPaidInstallments.push({
            ...paidBill,
            paymentDate: selectedPaymentDate,
            paymentMethod: paymentMethod,
            paymentPerson: paymentPerson,
            notes: receiptNote,
            appliedAmounts: [
              { installmentId: paidBill.id, amount: parseFloat(paidBill.amount) }
            ]
          });
          
          // GÜVENLI SİLME: Ödenen taksiti listeden çıkar (sadece seçilen olanı)
          updatedUnpaidBills = unpaidBills.filter(bill => 
            !(bill.id === selectedBill.id && 
              Math.abs(parseFloat(bill.amount) - parseFloat(selectedBill.amount)) < 0.01 &&
              bill.dueDate === selectedBill.dueDate)
          );
          
          // Mali kayıt oluştur
          
        } 
        // Nakit ödeme (seçilen taksit için)
        else if (paymentType === "nakit") {
          if (!selectedBill) {
            throw new Error("Nakit ödeme için taksit seçmelisiniz");
          }
          const paymentAmountNum = parseCurrency(paymentAmount);
          const billAmount = parseFloat(selectedBill.amount);
          
          // FLOAT KARŞILAŞTIRMA: Ondalık sayılar karşılaştırılırken hassasiyet sorunları olabileceğinden,
          // tam eşitlik yerine küçük bir tolerans değeriyle karşılaştırma yapıyoruz
          const EPSILON = 0.001; // Tolerans değeri
          const isApproximatelyEqual = Math.abs(paymentAmountNum - billAmount) < EPSILON;
          const isGreaterThan = paymentAmountNum > billAmount + EPSILON;
          const isLessThan = paymentAmountNum < billAmount - EPSILON;
          
          // Ödeme tutarı taksit tutarına eşit veya fazlaysa
          if (isApproximatelyEqual || isGreaterThan) {
            // Seçilen taksiti tamamen öde
            updatedPaidInstallments.push({
              ...selectedBill,
              paymentDate: selectedPaymentDate,
              paymentMethod: paymentMethod,
              paymentPerson: paymentPerson,
              paymentPersonSurname: paymentPersonSurname,
              notes: `Nakit ödeme: ${receiptNote}`,
              amount: billAmount, // Orijinal taksit tutarını kullan
              appliedAmounts: [
                { installmentId: selectedBill.id, amount: billAmount }
              ]
            });
            
            // GÜVENLI SİLME: Seçilen faturayı unpaidBills'den çıkar (sadece aynı tutar ve vade tarihine sahip olanı)
            updatedUnpaidBills = updatedUnpaidBills.filter(bill => 
              !(bill.id === selectedBill.id && 
                Math.abs(parseFloat(bill.amount) - billAmount) < 0.01 &&
                bill.dueDate === selectedBill.dueDate)
            );
            
            // Eğer fazla ödeme varsa, kalan miktarı sonraki taksitlere uygula
            if (isGreaterThan) {
              let remainingAmount = parseFloat((paymentAmountNum - billAmount).toFixed(2));
              
              // Seçilen taksit sonrasındaki taksitleri sırala (önce ID'ye, sonra vade tarihine göre)
              const nextBills = updatedUnpaidBills
                .filter(bill => bill.id !== selectedBill.id) // Seçilen taksit hariç tüm taksitler
                .sort((a, b) => {
                  // Önce ID'ye göre sırala
                  if (a.id !== b.id) return a.id - b.id;
                  // Sonra vade tarihine göre sırala
                  return new Date(a.dueDate) - new Date(b.dueDate);
                });
              
              const processedBillIds = [];
              
              for (const nextBill of nextBills) {
                if (remainingAmount <= 0) break;
                
                const nextBillAmount = parseFloat(nextBill.amount);
                
                // Kalan miktar bu taksiti tamamen kapatabilirse
                if (remainingAmount >= nextBillAmount - EPSILON) {
                  // Taksiti tamamen öde
                  updatedPaidInstallments.push({
                    ...nextBill,
                    paymentDate: selectedPaymentDate,
                    paymentMethod: paymentMethod,
                    paymentPerson: paymentPerson,
                    paymentPersonSurname: paymentPersonSurname,
                    notes: `Nakit ödeme fazlası: ${receiptNote}`,
                    amount: nextBillAmount,
                    appliedAmounts: [
                      { installmentId: nextBill.id, amount: nextBillAmount }
                    ]
                  });
                  
                  processedBillIds.push(nextBill.id);
                  remainingAmount = parseFloat((remainingAmount - nextBillAmount).toFixed(2));
                  
                  toast.success(`${nextBill.id} nolu taksit de ödendi (fazla ödemeden)`);
                } 
                // Kalan miktar taksiti kısmen ödeyebilirse
                else {
                  const paidAmount = remainingAmount;
                  const remainingBillAmount = parseFloat((nextBillAmount - paidAmount).toFixed(2));
                  
                  // Kısmi ödeme kaydı oluştur
                  const partialId = generatePartialPaymentId(nextBill.id, updatedPaidInstallments);
                  updatedPaidInstallments.push({
                    id: partialId,
                    amount: paidAmount,
                    originalAmount: nextBillAmount,
                    remainingAmount: remainingBillAmount,
                    dueDate: nextBill.dueDate,
                    paymentDate: selectedPaymentDate,
                    paymentMethod: paymentMethod,
                    paymentPerson: paymentPerson,
                    paymentPersonSurname: paymentPersonSurname,
                    notes: `${nextBill.id}. taksitin kısmi ödemesi (fazla ödemeden): ${receiptNote}`,
                    originalInstallmentId: nextBill.id,
                    partialPayment: true,
                    originalDueDate: nextBill.dueDate,
                    appliedAmounts: [
                      { installmentId: nextBill.id, amount: paidAmount }
                    ]
                  });
                  
                  // Orijinal taksiti güncelle
                  const updatedBill = {
                    ...nextBill,
                    originalAmount: nextBillAmount,
                    paidAmount: paidAmount,
                    amount: remainingBillAmount,
                    remainingAmount: remainingBillAmount
                  };
                  
                  // UnpaidBills'de güncelle
                  updatedUnpaidBills = updatedUnpaidBills.map(bill => 
                    bill.id === nextBill.id ? updatedBill : bill
                  );
                  
                  toast.success(`${nextBill.id} nolu taksite ${paidAmount} ₺ kısmi ödeme yapıldı (fazla ödemeden). Kalan: ${remainingBillAmount} ₺`);
                  remainingAmount = 0;
                  break;
                }
              }
              
              // İşlenen taksitleri unpaidBills'den çıkar
              updatedUnpaidBills = updatedUnpaidBills.filter(bill => !processedBillIds.includes(bill.id));
              
              // Eğer hala kalan miktar varsa uyar
              if (remainingAmount > 0) {
                toast.warning(`${selectedBill.id} nolu taksit ödendi, ${remainingAmount} ₺ ek ödeme kaldı (başka ödenmemiş taksit yok)`);
              } else {
                toast.success(`${selectedBill.id} nolu taksit ödendi ve fazla ödeme sonraki taksitlere uygulandı`);
              }
            } else {
              toast.success(`${selectedBill.id} nolu taksit tamamen ödendi`);
            }
          } 
          // Ödeme tutarı taksit tutarından küçükse (kısmi ödeme)
          else if (isLessThan) {
            const paidAmount = paymentAmountNum;
            const remainingBillAmount = parseFloat((billAmount - paidAmount).toFixed(2));
            
            // Kısmi ödeme kaydı oluştur
            const partialId = generatePartialPaymentId(selectedBill.id, updatedPaidInstallments);
            updatedPaidInstallments.push({
              id: partialId,
              amount: paidAmount,
              originalAmount: billAmount,
              remainingAmount: remainingBillAmount,
              dueDate: selectedBill.dueDate,
              paymentDate: selectedPaymentDate,
              paymentMethod: paymentMethod,
              paymentPerson: paymentPerson,
              paymentPersonSurname: paymentPersonSurname,
              notes: `${selectedBill.id}. taksitin kısmi ödemesi: ${receiptNote}`,
              originalInstallmentId: selectedBill.id,
              partialPayment: true,
              originalDueDate: selectedBill.dueDate,
              appliedAmounts: [
                { installmentId: selectedBill.id, amount: paidAmount }
              ]
            });
            
            // Orijinal taksiti güncelle (kalan miktarla)
            const updatedBill = {
              ...selectedBill,
              originalAmount: billAmount,
              paidAmount: paidAmount,
              amount: remainingBillAmount,
              remainingAmount: remainingBillAmount
            };
            
            // UnpaidBills'de güncelle
            updatedUnpaidBills = updatedUnpaidBills.map(bill => 
              bill.id === selectedBill.id ? updatedBill : bill
            );
            
            toast.success(`${selectedBill.id} nolu taksite ${paidAmount} ₺ kısmi ödeme yapıldı. Kalan: ${remainingBillAmount} ₺`);
          }
        }

        // Güncellenmiş ödeme planını oluştur
        const updatedPaymentPlan = {
          ...parsedPaymentPlan,
          unpaidBills: updatedUnpaidBills,
          paidInstallments: updatedPaidInstallments
        };
        
        // Toplam değerleri doğru olduğundan emin ol
        // Mevcut toplam, ödenmiş ve kalan tutarları kontrol et
        const totalPaidAmount = updatedPaidInstallments.reduce(
          (total, installment) => total + parseFloat(installment.amount || 0), 0
        );
        
        const totalUnpaidAmount = updatedUnpaidBills.reduce(
          (total, bill) => total + parseFloat(bill.amount || 0), 0
        );
        
        // Toplam tutarın ödenen + kalan tutarlara eşit olduğundan emin ol
        updatedPaymentPlan.totalAmount = totalPaidAmount + totalUnpaidAmount;
        
        // Ödeme planını güncelle
        const paymentPlanJson = JSON.stringify(updatedPaymentPlan);
        
      

        // Öğrenci verisini API'de güncelle
        axios.put(`http://esnakademi.com/api/students/update?id=${prevStudent.id}`, {
          id: prevStudent.id,
          name: prevStudent.name,
          surname: prevStudent.surname,
          student_number: prevStudent.student_number,
          student_class: prevStudent.student_class,
          period: prevStudent.period || "",
          registration_date: prevStudent.registration_date || "",
          branch: prevStudent.branch || "",
          phone: prevStudent.phone || "",
          email: prevStudent.email || "",
          photo_url: prevStudent.photo_url || "",
          gender: prevStudent.gender || "",
          birth_date: prevStudent.birth_date || "",
          tc_number: prevStudent.tc_number || "",
          address: prevStudent.address || "",
          advisor_teacher: prevStudent.advisor_teacher || "",
          mother_name: prevStudent.mother_name || "",
          mother_phone: prevStudent.mother_phone || "",
          mother_email: prevStudent.mother_email || "",
          mother_address: prevStudent.mother_address || "",
          father_name: prevStudent.father_name || "",
          father_phone: prevStudent.father_phone || "",
          father_email: prevStudent.father_email || "",
          father_address: prevStudent.father_address || "",
          school: prevStudent.school || "",
          payment_responsible: prevStudent.payment_responsible || "",
          responsible_tc_number: prevStudent.responsible_tc_number || "",
          payment_plan: paymentPlanJson
        }, {
          headers: getRequestHeaders()
        })
        .then(async (response) => {
      
          toast.success("Öğrenci ödeme planı başarıyla güncellendi");
          // Güncelleme callbackini çağır
          if (onUpdate && typeof onUpdate === 'function') {
            // Ödeme tutarını sayısal değer olarak gönder
            const numericAmount = parseCurrency(paymentAmount);
            onUpdate({
              ...prevStudent,
              payment_plan: paymentPlanJson,
              _paymentData: {
                amount: numericAmount, // String değil, sayısal değer gönder
                method: paymentMethod,
                type: paymentType,
                notes: receiptNote,
                paymentDate: selectedPaymentDate,
                timestamp: Date.now()
              }
            });
          }
          
          // Makbuz kaydını backend'e kaydet
          try {
            const receiptData = {
              student_id: updatedStudent.id,
              receipt_number: `${updatedStudent.student_number || "00000000"}-${String(selectedBill?.id || "000").padStart(3,"0")}`,
              payment_date: selectedPaymentDate,
              receipt_date: new Date().toISOString().split("T")[0],
              total_amount: parseCurrency(paymentAmount),
              payment_method: paymentMethod.toUpperCase(),
              payment_person: paymentPerson,
              payment_person_surname: paymentPersonSurname || "",
              receipt_user: getUserNameFromToken(),
              remaining_total_debt: getTotalRemainingAmount(updatedUnpaidBills),
              contract_number: updatedStudent.student_number || null,
              notes: receiptNote || null,
              paid_installments: [{
                installment_id: selectedBill?.id || "000",
                due_date: selectedBill?.dueDate || new Date().toISOString().split("T")[0],
                amount: parseCurrency(paymentAmount),
                payment_type: "Normal Ödeme",
                original_amount: selectedBill?.amount || parseCurrency(paymentAmount)
              }]
            };

            // Backend'e makbuz kaydını gönder
            await axios.post('http://esnakademi.com/api/receipts', receiptData, {
              headers: getRequestHeaders()
            });

            console.log("Makbuz backend'e kaydedildi!");
          } catch (receiptError) {
            console.error("Makbuz kaydetme hatası:", receiptError);
            // Makbuz kaydetme hatası ödeme işlemini durdurmasın
          }

          // Ödeme başarılı, onay göster
          setShowConfirmation(true);
        })
        .catch(error => {
          console.error("Öğrenci güncelleme hatası:", error);
          toast.error("Ödeme planı güncellenirken bir hata oluştu");
          if (error.response) {
            console.error("API Hata Detayı:", error.response.data);
          }
        });

        // Lokal güncellemeyi hemen göster (optimistic update)
        return {
          ...prevStudent,
          payment_plan: paymentPlanJson
        };
      });

      // Ödeme başarılı, onay göster
      toast.success("Ödeme işlemi tamamlanıyor...");
    } catch (error) {
      console.error("Ödeme işlemi hatası:", error);
      toast.error("Ödeme işlemi sırasında bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const generateReceipt = async () => {
    try {
      // Kalan toplam borç tutarını hesapla
      const remainingTotalDebt = parsedPaymentPlan?.unpaidBills?.reduce(
        (total, bill) => total + parseFloat(bill.amount || 0), 0
      ) || 0;

      // Fazla ödeme durumunda ödenen taksitleri hesapla
      const paymentAmountNum = parseCurrency(paymentAmount);
      const billAmount = selectedBill ? parseFloat(selectedBill.amount) : paymentAmountNum;
      const isGreaterThan = paymentAmountNum > billAmount + 0.01;
      
      let paidInstallments = [];
      
      if (isGreaterThan && selectedBill) {
        // Fazla ödeme durumunda ödenen taksitleri hesapla
        let remainingAmount = paymentAmountNum;
        
        // 1. İlk taksit (seçilen taksit)
        paidInstallments.push({
          id: selectedBill.id,
          dueDate: selectedBill.dueDate,
          amount: billAmount,
          paymentType: "Tam Ödeme",
          originalAmount: billAmount
        });
        remainingAmount -= billAmount;
        
        // 2. Sonraki taksitler - seçilen taksit hariç tüm taksitler (ID'ye göre sıralı)
        const nextBills = parsedPaymentPlan?.unpaidBills?.filter(bill => bill.id !== selectedBill.id)
          .sort((a, b) => a.id - b.id) || [];
        
        for (const nextBill of nextBills) {
          if (remainingAmount <= 0) break;
          
          const nextBillAmount = parseFloat(nextBill.amount);
          
          if (remainingAmount >= nextBillAmount - 0.01) {
            // Tam ödeme
            paidInstallments.push({
              id: nextBill.id,
              dueDate: nextBill.dueDate,
              amount: nextBillAmount,
              paymentType: "Fazla Ödeme (Tam)",
              originalAmount: nextBillAmount
            });
            remainingAmount -= nextBillAmount;
          } else {
            // Kısmi ödeme
            paidInstallments.push({
              id: nextBill.id,
              dueDate: nextBill.dueDate,
              amount: remainingAmount,
              paymentType: "Fazla Ödeme (Kısmi)",
              originalAmount: nextBillAmount
            });
            remainingAmount = 0;
            break;
          }
        }
      } else {
        // Normal ödeme durumu
        paidInstallments.push({
          id: selectedBill ? selectedBill.id : "GENEL",
          dueDate: selectedBill ? selectedBill.dueDate : paymentDate,
          amount: paymentAmountNum,
          paymentType: "Normal Ödeme",
          originalAmount: selectedBill ? selectedBill.amount : paymentAmountNum
        });
      }

      // Güncel ödeme verilerini kullanarak makbuz oluştur
      const currentPaymentData = {
        id: selectedBill ? selectedBill.id : "GENEL",
        dueDate: selectedBill ? selectedBill.dueDate : paymentDate,
        amount: parseCurrency(paymentAmount),
        paymentDate: paymentDate,
        paymentMethod: paymentMethod.toUpperCase(),
        paymentPerson: paymentPerson,
        paymentPersonSurname: paymentPersonSurname,
        originalAmount: selectedBill ? selectedBill.amount : parseCurrency(paymentAmount)
      };

      // Makbuz verilerini hazırla - StudentDetails ile birebir aynı format
      const makbuzData = {
        student: updatedStudent,
        installment: currentPaymentData,
        paymentDate: currentPaymentData.paymentDate,
        receiptDate: new Date().toISOString().split("T")[0],
        receiptNumber: selectedBill ? `${updatedStudent.student_number || "00000000"}-${String(selectedBill.id).padStart(3,"0")}` : `${updatedStudent.student_number || "00000000"}-000`,
        totalAmount: currentPaymentData.amount,
        paymentMethod: currentPaymentData.paymentMethod,
        paymentPerson: currentPaymentData.paymentPerson,
        paymentPersonSurname: currentPaymentData.paymentPersonSurname,
        remainingTotalDebt: remainingTotalDebt,
        receiptUser: getUserNameFromToken(), // Makbuzu alan kullanıcının adı
        paidInstallments: paidInstallments // Ödenen taksitler listesi
      };

      // Makbuz verilerini state'e kaydet ve modal'ı aç
      setMakbuzData(makbuzData);
      setIsMakbuzModalOpen(true);
      
    } catch (error) {
      console.error("Makbuz oluşturma hatası:", error);
      toast.error("Makbuz oluşturulurken bir hata oluştu");
    }
  };

  const handleRadioChange = (bill) => {
    // Önce ödeme yapan kişi seçilmiş mi kontrol et
    if (!paymentPerson || paymentPerson.trim() === "") {
      toast.warning("⚠️ Önce Ödemeyi Yapan Kişiyi Seçin!", {
        duration: 5000,
        style: {
          background: '#FEF3C7',
          color: '#92400E',
          fontWeight: 'bold',
          fontSize: '14px',
          border: '2px solid #F59E0B',
          borderRadius: '8px'
        }
      });
      // Ödeme yapan kişi alanına focus ver
      const paymentPersonSelect = document.querySelector('select');
      if (paymentPersonSelect && paymentPersonSelect.value === paymentPerson) {
        paymentPersonSelect.focus();
        paymentPersonSelect.style.border = '2px solid #F59E0B';
        paymentPersonSelect.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
        setTimeout(() => {
          paymentPersonSelect.style.border = '';
          paymentPersonSelect.style.boxShadow = '';
        }, 3000);
      }
      return;
    }
    
    // Aynı senet seçiliyse iptal et, değilse yeni seneti seç
    if (selectedBill?.id === bill.id) {
      
      setSelectedBill(null);
      setPaymentAmount("");
    } else {
      
      setSelectedBill(bill);
      // Taksit ödemesinde otomatik olarak taksit tutarını ayarla
      if (paymentType === "taksit") {
        
        setPaymentAmount(bill.amount.toString());
      }
    }
  };

  // İşlem sonrası ekran (başarılı ödeme durumu)
  if (showConfirmation) {
    return (
      <div className="fixed inset-0 w-full bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
        <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 max-w-sm sm:max-w-md w-full shadow-2xl transform transition-all">
          <div className="text-center">
            <FaCheckCircle className="mx-auto text-green-500 text-3xl sm:text-4xl lg:text-5xl mb-3 sm:mb-4" />
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">Ödeme Başarılı!</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              {paymentAmount} ₺ tutarındaki ödeme başarıyla kaydedildi.
            </p>
            
            <div className="flex flex-col space-y-2 sm:space-y-3">
              <button
                onClick={generateReceipt}
                className="w-full py-2 sm:py-3 px-3 sm:px-4 text-sm sm:text-base bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center"
              >
                <FaPrint className="mr-1 sm:mr-2 text-sm sm:text-base" /> Makbuz Yazdır
              </button>
              
              <button
                onClick={resetForm}
                className="w-full py-2 sm:py-3 px-3 sm:px-4 text-sm sm:text-base bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Yeni Ödeme
              </button>
              
              <button
                onClick={closeModal}
                className="w-full py-2 sm:py-3 px-3 sm:px-4 text-sm sm:text-base bg-white text-gray-700 border border-gray-300 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
        
        {/* Makbuz Modalı - showConfirmation durumunda */}
        {isMakbuzModalOpen && (
          <MakbuzModal
            makbuzData={makbuzData}
            closeModal={() => setIsMakbuzModalOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <>
    <div className="fixed inset-0 w-full bg-black bg-opacity-50 flex justify-center items-start sm:items-center z-50 overflow-y-auto p-4">
      <div className="relative bg-white rounded-xl shadow-2xl max-w-sm sm:max-w-2xl lg:max-w-4xl w-full my-4 sm:my-8 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-3 sm:p-4 lg:p-6 text-white">
          <div className="flex justify-between items-center">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold">Ödeme İşlemi</h3>
            <button
              onClick={closeModal}
              className="text-white hover:text-gray-200 focus:outline-none transition-colors p-1"
            >
              <FaTimes className="text-lg sm:text-xl" />
            </button>
          </div>
          <p className="mt-1 text-sm sm:text-base text-blue-100">
            {student.name} {student.surname} için ödeme alın
          </p>
        </div>

        <form onSubmit={handlePaymentSubmit} className="p-3 sm:p-4 lg:p-6">
          {/* Ödeme Türü Seçimi */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ödeme Türü
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => handlePaymentTypeChange("taksit")}
                className={`flex items-center justify-center p-3 sm:p-4 rounded-lg border-2 transition-all ${
                  paymentType === "taksit"
                    ? "border-blue-600 bg-blue-50 text-blue-700"
                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                }`}
              >
                <FaFileInvoiceDollar className="mr-1 sm:mr-2 text-sm sm:text-lg" />
                <span className="font-medium text-sm sm:text-base">Taksit Ödemesi</span>
              </button>
              
              
              
              <button
                type="button"
                onClick={() => handlePaymentTypeChange("nakit")}
                className={`flex items-center justify-center p-3 sm:p-4 rounded-lg border-2 transition-all ${
                  paymentType === "nakit"
                    ? "border-blue-600 bg-blue-50 text-blue-700"
                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                }`}
              >
                <FaCreditCard className="mr-1 sm:mr-2 text-sm sm:text-lg" />
                <span className="font-medium text-sm sm:text-base">Nakit Ödeme</span>
              </button>
            </div>
          </div>

          {/* Ödeme Yapan Kişi Seçimi */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ödeme Yapan Kişi <span className="text-red-500">*</span>
            </label>
            <select
              value={paymentPerson}
              onChange={(e) => handlePaymentPersonChange(e.target.value)}
              required
              className={`block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                !paymentPerson || paymentPerson.trim() === "" ? 'border-orange-300 bg-orange-50' : ''
              }`}
            >
              <option value="">Seçiniz</option>
              <option value="anne">Anne ({student.parent_name || student.mother_name || 'Belirtilmemiş'})</option>
              <option value="baba">Baba ({student.father_name || 'Belirtilmemiş'})</option>
              <option value="diger">Diğer</option>
            </select>
            {(!paymentPerson || paymentPerson.trim() === "") && (
              <p className="mt-2 text-sm text-orange-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Taksit seçebilmek için önce ödeme yapan kişiyi seçin
              </p>
            )}
          </div>


          {/* Taksit Listesi (Taksit, Kısmi veya Nakit Ödeme türü seçildiğinde) */}
          {(paymentType === "taksit" || paymentType === "kismi" || paymentType === "nakit") && (
            <div className="mb-4 sm:mb-6 border border-gray-200 rounded-lg">
              <div 
                className="flex justify-between items-center p-3 sm:p-4 bg-gray-50 cursor-pointer" 
                onClick={toggleBillList}
              >
                <h4 className="text-base sm:text-lg font-medium text-gray-800 flex items-center">
                  <FaRegCalendarAlt className="mr-1 sm:mr-2 text-blue-600 text-sm sm:text-base" />
                  {paymentType === "nakit" 
                    ? "Hangi Taksit için Ödeme?" 
                    : userRole === "admin" 
                      ? "Ödenmemiş Taksitler" 
                      : "İlk Taksit (Personel)"
                  }
            </h4>
                {showBillList ? <FaChevronUp className="text-sm sm:text-base" /> : <FaChevronDown className="text-sm sm:text-base" />}
              </div>
              
              {showBillList && (
                <div className="max-h-64 overflow-y-auto">
                  {userRole === "user" && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                      <p className="text-sm text-orange-700">
                        <strong>Personel Kısıtlaması:</strong> Sadece kronolojik olarak ilk sıradaki taksiti alabilirsiniz. Diğer taksitler için admin ile iletişime geçin.
                      </p>
                    </div>
                  )}
                
                  {parsedPaymentPlan.unpaidBills && parsedPaymentPlan.unpaidBills.length > 0 ? (
                    <>
                      {/* Mobil Görünüm */}
                      <div className="block sm:hidden">
                        {Array.isArray(parsedPaymentPlan.unpaidBills) && getAvailableBills(parsedPaymentPlan.unpaidBills)
                          .slice()
                          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                          .map((bill, index) => {
                            const isPastDue = new Date(bill.dueDate) < new Date();
                            return (
                              <div
                                key={bill.id || `unpaid-${index}`}
                                className={`p-4 border-b border-gray-200 last:border-b-0 ${
                                  !paymentPerson || paymentPerson.trim() === "" 
                                    ? 'cursor-not-allowed opacity-50 bg-gray-100' 
                                    : `cursor-pointer ${selectedBill?.id === bill.id ? 'bg-blue-50' : isPastDue ? 'bg-red-50' : 'hover:bg-gray-50'}`
                                }`}
                                onClick={() => handleRadioChange(bill)}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <span className="font-medium text-gray-900">{bill.id || index+1}. Taksit</span>
                                  <input
                                    type="radio"
                                    name="selectedBill"
                                    checked={selectedBill?.id === bill.id}
                                    onChange={() => {}}
                                    disabled={!paymentPerson || paymentPerson.trim() === ""}
                                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  />
                                </div>
                                <div className="text-sm text-gray-600 space-y-1">
                                  <div>Tutar: {bill.amount} ₺</div>
                                  <div className="flex items-center">
                                    Vade: {bill.dueDate}
                                    {isPastDue && (
                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Gecikmiş
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>

                      {/* Desktop Görünüm */}
                      <table className="hidden sm:table min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Taksit No
                            </th>
                            <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Tutar
                            </th>
                            <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Vade Tarihi
                            </th>
                            <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Seç
                            </th>
                    </tr>
                  </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        
                          {Array.isArray(parsedPaymentPlan.unpaidBills) && getAvailableBills(parsedPaymentPlan.unpaidBills)
                            .slice()
                            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                            .map((bill, index) => {
                      
                              const isPastDue = new Date(bill.dueDate) < new Date();
                              return (
                                <tr 
                                  key={bill.id || `unpaid-${index}`} 
                                  className={`${
                                    !paymentPerson || paymentPerson.trim() === "" 
                                      ? 'cursor-not-allowed opacity-50 bg-gray-100' 
                                      : `${selectedBill?.id === bill.id ? 'bg-blue-50' : ''} ${isPastDue ? 'bg-red-50' : ''} hover:bg-gray-50 cursor-pointer`
                                  }`}
                                  onClick={() => handleRadioChange(bill)}
                                >
                                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {bill.id || index+1}
                                  </td>
                                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {bill.amount} ₺
                                  </td>
                                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {bill.dueDate}
                                    {isPastDue && (
                                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Gecikmiş
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <input
                              type="radio"
                              name="selectedBill"
                                      checked={selectedBill?.id === bill.id}
                                      onChange={() => {}} // onChange gerekli ama event handler yukarıda
                                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                        </tr>
                              );
                            })}
                  </tbody>
                </table>
                      </>
                  ) : (
                    <div className="p-6 text-center text-gray-500">
                      Ödenmemiş taksit bulunmamaktadır.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Kalan Toplam Tutar Bilgisi */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-blue-800">Kalan Toplam Tutar:</span>
              <span className="text-lg font-bold text-blue-900">
                {getTotalRemainingAmount(parsedPaymentPlan.unpaidBills).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Ödeme Tutarı */}
            <div>
              <label htmlFor="paymentAmount" className="block text-sm font-medium text-gray-700 mb-2">
                Ödeme Tutarı (₺)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaMoneyBillWave className="text-gray-400" />
                </div>
                <input
                  type="text"
                  id="paymentAmount"
                  value={paymentAmount}
                  onChange={(e) => {
                    if (paymentType !== "taksit") {
                      const formatted = formatCurrencyInput(e.target.value);
                      handlePaymentAmountChange(formatted);
                    }
                  }}
                  onBlur={(e) => {
                    if (paymentType === "nakit" && e.target.value && e.target.value.trim() !== '') {
                      const numericValue = parseCurrency(e.target.value);
                      if (!isNaN(numericValue) && numericValue > 0) {
                        const rounded = Math.round(numericValue / 50) * 50;
                        setPaymentAmount(formatCurrencyInput(rounded.toString()));
                      }
                    }
                  }}
                  placeholder="0,00"
                  required
                  className={`pl-10 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    paymentError ? 'border-red-500 bg-red-50' : ''
                  } ${
                    paymentType === "taksit" ? "bg-gray-100" : ""
                  }`}
                  readOnly={paymentType === "taksit"} // Taksit ödemesinde tutar değiştirilemez
                />
              </div>
              {paymentError && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {paymentError}
                </p>
              )}
            </div>

            {/* Ödeme Metodu */}
            <div>
              <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-2">
                Ödeme Şekli
              </label>
              <select
                id="paymentMethod"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="nakit">Nakit</option>
                <option value="kart">Kredi Kartı</option>
                <option value="havale">Havale/EFT</option>
                <option value="diger">Diğer</option>
              </select>
            </div>

            {/* Ödeme Tarihi */}
            <div className="mt-4">
              <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700 mb-2">
                Ödeme Tarihi
                {userRole === "admin" && <span className="text-blue-600 text-xs ml-1">(Admin: Tarih seçebilir)</span>}
                {userRole === "user" && <span className="text-gray-500 text-xs ml-1">(Bugünün tarihi)</span>}
              </label>
              <input
                type="date"
                id="paymentDate"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                disabled={userRole === "user"}
                className={`block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                  userRole === "user" ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
              />
              {userRole === "user" && (
                <p className="mt-1 text-xs text-gray-500">
                  Sadece admin kullanıcıları ödeme tarihini değiştirebilir
                </p>
              )}
            </div>
           
          </div>

          {/* Not Alanı */}
          <div className="mt-4">
            <label htmlFor="receiptNote" className="block text-sm font-medium text-gray-700 mb-2">
              Not (Opsiyonel)
            </label>
            <textarea
              id="receiptNote"
              value={receiptNote}
              onChange={(e) => setReceiptNote(e.target.value)}
              rows="3"
              placeholder="Ödeme ile ilgili eklemek istediğiniz notlar..."
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            ></textarea>
          </div>

          {/* Kağıt Boyutu */}
          <div className="mt-4">
            <label htmlFor="paperSize" className="block text-sm font-medium text-gray-700 mb-2">
              Makbuz Kağıt Boyutu
            </label>
            <select
              id="paperSize"
              value={paperSize}
              onChange={(e) => setPaperSize(e.target.value)}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="A4">A4</option>
              <option value="A5">A5</option>
            </select>
          </div>

          {/* Alt Butonlar */}
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              onClick={closeModal}
              className="w-full sm:w-auto py-2 sm:py-3 px-3 sm:px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 order-2 sm:order-1"
            >
              İptal
            </button>
            
              <button
                type="submit"
                disabled={loading || (!selectedBill && (paymentType === "taksit" || paymentType === "kismi" || paymentType === "nakit")) || !paymentAmount || !paymentPerson}
                className={`w-full sm:w-auto py-2 sm:py-3 px-3 sm:px-4 rounded-md shadow-sm text-sm font-medium text-white order-1 sm:order-2 flex items-center justify-center
                  ${
                    loading || (!selectedBill && (paymentType === "taksit" || paymentType === "kismi" || paymentType === "nakit")) || !paymentAmount || !paymentPerson
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    İşlem Yapılıyor...
                  </>
                ) : (
                  "Ödeme Al"
                )}
              </button>
            </div>
          </form>
      </div>
    </div>

    {/* Makbuz Modalı */}
    {isMakbuzModalOpen && (
      <MakbuzModal
        makbuzData={makbuzData}
        closeModal={() => setIsMakbuzModalOpen(false)}
      />
    )}
    </>
  );
};

export default PaymentModal;
